/**
 * @description SignIn component for Wristband authentication (Self-Hosted UI)
 *
 * Key features:
 * - Email/password form with validation
 * - Auto-redirects to start OAuth flow if no authorization token exists
 * - Stores authorizationRequestToken in sessionStorage (keeps URL clean)
 * - Calls POST /api/auth/login with credentials + authorizationRequestToken
 * - Follows redirect URL on success to complete OAuth flow
 * - Brand accent colors via CSS custom properties (--brand-primary)
 *
 * @dependencies
 * - AuthLayout: Shared auth page layout (dual-mode: LynxPay marketing / tenant simple)
 * - BootSplashDismisser: hides HTML #boot-splash after the form or error commits
 * - apiClient: API calls to backend
 *
 * @notes
 * - SO users must never remain on app.lynxpay.us. Wristband tenant_name /
 *   wristbandTenant / req van_dom are forwarded to GET /login. Platform-host
 *   SO ?req= (van_dom / non-LynxPay tnt_id) bounces through login-redirect.
 *   A LynxPay-classified req stays on the platform form (no redirect loop).
 * - Self-hosted login flow per Wristband docs:
 *   1. User clicks "Sign In" on tenant's website/app
 *   2. User lands on /sign-in -> auto-redirects to GET /api/auth/login
 *   3. Backend redirects to Wristband /authorize
 *   4. Wristband redirects back to /sign-in?req=<JWT>
 *   5. Component stores token in sessionStorage, cleans URL to /sign-in
 *   LYN-453: ?error= clears that token. authWindowMs (from the backend redirect)
 *   ages a stored req from a local timestamp. A stale tab restarts once.
 *   The typed email is kept in sessionStorage across that restart.
 *   6. User sees clean login form, enters credentials
 *   7. Frontend POSTs to /api/auth/login with {email, password, authorizationRequestToken}
 *   8. Backend returns redirectUrl -> Frontend follows it
 *   9. Wristband creates auth session -> redirects to /api/auth/callback
 *   10. Backend exchanges code for tokens -> redirects to /auth/callback#access_token=...
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, FileText, Users, Calculator } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import AuthLayout from './AuthLayout';
import { BootSplashDismisser, BootSplashKeeper } from './AuthBootSplash';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { isSignupChromeAllowed } from '../../utils/authSignupChrome';
import { isPlatformHostname, usesTenantBffOrigin } from '../../utils/lynxBuildHostname';
import { shouldBouncePlatformReqToSo } from '../../utils/wristbandReqClassify';
import { resolveTenantForHostname } from '../../services/api/tenantResolveService';
import {
  resolveOAuthUrlError,
  resolveSignInError,
  shouldRestartSignInFlow,
} from '../../utils/authErrorMessages';

// Backend base URL for redirects (without /api suffix).
// Fallback for non-proxied environments (localhost, direct Railway access).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

/**
 * Build a tenant-aware login URL for the backend OAuth flow.
 * BFF hosts (Tier-2 *.lynx.build + verified Tier-3): same-origin.
 * Platform / localhost: REACT_APP_BACKEND_URL.
 *
 * @param {string|null} subdomain
 * @returns {URL}
 */
function buildTenantLoginUrl(subdomain, extraParams = {}) {
  const hostname = window.location.hostname;

  const baseUrl = usesTenantBffOrigin(hostname)
    ? window.location.origin
    : BACKEND_URL;

  const loginUrl = new URL(`${baseUrl}/api/auth/login`);
  if (subdomain) {
    loginUrl.searchParams.set('subdomain', subdomain);
  }
  for (const [key, value] of Object.entries(extraParams)) {
    if (value) {
      loginUrl.searchParams.set(key, value);
    }
  }
  return loginUrl;
}

/**
 * Wristband Login Endpoint / minted-link signals that identify an SO user.
 *
 * @param {URLSearchParams} params
 * @returns {{ subdomain: string|null, wristbandTenant: string|null, tenant_name: string|null, tenant_id: string|null, tenant_custom_domain: string|null }}
 */
function collectWristbandLoginSignals(params) {
  return {
    subdomain: params.get('subdomain'),
    wristbandTenant: params.get('wristbandTenant'),
    tenant_name: params.get('tenant_name'),
    tenant_id: params.get('tenant_id') || params.get('tnt_id'),
    tenant_custom_domain: params.get('tenant_custom_domain')
  };
}

// SessionStorage key for authorization request token
const AUTH_REQUEST_TOKEN_KEY = 'wristband_auth_request_token';
const AUTH_REQUEST_STORED_AT_KEY = 'wristband_auth_request_stored_at';
const AUTH_WINDOW_MS_KEY = 'wristband_auth_window_ms';
const SIGN_IN_EMAIL_KEY = 'wristband_sign_in_email';
const SIGN_IN_RESTART_ONCE_KEY = 'wristband_sign_in_restart_once';
// SessionStorage key for post-login redirect (e.g. accept-company-invite)
const RETURN_URL_KEY = 'auth_return_url';

/**
 * Positive millisecond window, or null when missing or unusable.
 *
 * @param {string|null|undefined} raw
 * @returns {number|null}
 */
function parsePositiveMs(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function clearStoredAuthRequest() {
  sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_REQUEST_STORED_AT_KEY);
  sessionStorage.removeItem(AUTH_WINDOW_MS_KEY);
}

/**
 * @param {string} reqToken
 * @param {number|null} windowMs
 */
function storeAuthRequest(reqToken, windowMs) {
  sessionStorage.setItem(AUTH_REQUEST_TOKEN_KEY, reqToken);
  sessionStorage.setItem(AUTH_REQUEST_STORED_AT_KEY, String(Date.now()));
  if (windowMs) {
    sessionStorage.setItem(AUTH_WINDOW_MS_KEY, String(windowMs));
  } else {
    sessionStorage.removeItem(AUTH_WINDOW_MS_KEY);
  }
  sessionStorage.removeItem(SIGN_IN_RESTART_ONCE_KEY);
}

/**
 * True when the stored req is at or past the backend window.
 * Missing, malformed, or non-positive window skips the check.
 *
 * @returns {boolean}
 */
function storedAuthRequestIsStale() {
  const windowMs = parsePositiveMs(sessionStorage.getItem(AUTH_WINDOW_MS_KEY));
  if (!windowMs) {
    return false;
  }
  const storedAt = Number(sessionStorage.getItem(AUTH_REQUEST_STORED_AT_KEY));
  if (!Number.isFinite(storedAt) || storedAt <= 0) {
    return false;
  }
  return Date.now() - storedAt >= windowMs;
}

/**
 * Save the protected route from a definitive refresh failure before OAuth.
 * AuthCallback consumes auth_return_url. Invite ?returnUrl= is unchanged
 * when location.state.from is absent.
 *
 * @param {object} location - react-router location
 */
function persistProtectedReturnPath(location) {
  const from = location.state?.from;
  const pathname = from?.pathname;
  if (!pathname || !pathname.startsWith('/') || pathname.startsWith('//') || pathname === '/sign-in') {
    return;
  }
  const returnPath = pathname + (from.search || '') + (from.hash || '');
  if (!returnPath.startsWith('/') || returnPath.startsWith('//')) {
    return;
  }
  sessionStorage.setItem(RETURN_URL_KEY, returnPath);
}

function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const { signupEnabled } = useBranding();
  const showSignupChrome = isSignupChromeAllowed({ signupEnabled });

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Auth state - the authorization request token from Wristband
  const [authorizationRequestToken, setAuthorizationRequestToken] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const savedEmail = sessionStorage.getItem(SIGN_IN_EMAIL_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      sessionStorage.removeItem(SIGN_IN_EMAIL_KEY);
    }
  }, []);

  // Features for left marketing panel
  const signInFeatures = [
    {
      icon: Layout,
      title: 'Unified operations',
      description: 'Streamline your daily business tasks in one interface.',
    },
    {
      icon: FileText,
      title: 'Professional invoicing',
      description: 'Automated accounts receivable and custom invoices.',
    },
    {
      icon: Calculator,
      title: 'Accounting',
      description: 'Robust tools to replace fragmented accounting and cut costs.',
    },
    {
      icon: Users,
      title: 'Employee management',
      description: 'Native scheduling and team management for your staff.',
    },
  ];

  /**
   * If user is already signed in, redirect to dashboard
   * This prevents the redirect loop when user has valid session
   */
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
      navigate('/dashboard', { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  /**
   * On mount, handle the OAuth flow per Wristband self-hosted UI docs:
   *
   * 1. If ?req= exists in URL: Store token in sessionStorage, clean URL, show form
   * 2. If token exists in sessionStorage: Use it, show form
   * 3. If no token anywhere: Auto-redirect to start OAuth flow
   */
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) return;

    const params = new URLSearchParams(location.search);
    const reqFromUrl = params.get('req');
    const urlError = params.get('error');

    // Preserve returnUrl (e.g. from AcceptCompanyInvite) through the OAuth flow
    const returnUrl = params.get('returnUrl');
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }

    // Surface OAuth redirect errors (e.g. session_expired, login_failed).
    // Stay on the form so the user can read the message and retry sign-in.
    if (urlError) {
      clearStoredAuthRequest();
      setError(resolveOAuthUrlError(urlError));
      window.history.replaceState({}, '', location.pathname);
      setIsCheckingAuth(false);
      return;
    }

    const loginSignals = collectWristbandLoginSignals(params);

    // Wristband Application Login URL is often app.lynxpay.us. Classify
    // ?req= before bouncing: SO vanity / non-LynxPay tnt_id → login-redirect.
    // LynxPay req stays here (sessionStorage) so authorize does not loop.
    if (
      reqFromUrl
      && isPlatformHostname(window.location.hostname)
      && shouldBouncePlatformReqToSo(reqFromUrl)
    ) {
      const redirect = new URL(`${BACKEND_URL}/api/auth/login-redirect`);
      redirect.searchParams.set('req', reqFromUrl);
      window.location.href = redirect.toString();
      return;
    }

    if (reqFromUrl) {
      storeAuthRequest(reqFromUrl, parsePositiveMs(params.get('authWindowMs')));
      setAuthorizationRequestToken(reqFromUrl);
      window.history.replaceState({}, '', location.pathname);
      setIsCheckingAuth(false);
      return;
    }

    const storedToken = sessionStorage.getItem(AUTH_REQUEST_TOKEN_KEY);
    if (storedToken) {
      setAuthorizationRequestToken(storedToken);
      setIsCheckingAuth(false);
      return;
    }

    let cancelled = false;

    (async () => {
      const hostname = window.location.hostname;
      const resolved = await resolveTenantForHostname(hostname);
      if (cancelled) return;

      // Custom host that is not a verified SO → fail closed (no platform OAuth).
      if (!isPlatformHostname(hostname)
          && !hostname.endsWith('.lynx.build')
          && !resolved?.slug) {
        setError('This domain is not configured for sign-in.');
        setIsCheckingAuth(false);
        return;
      }

      persistProtectedReturnPath(location);
      window.location.href = buildTenantLoginUrl(
        resolved?.slug || loginSignals.subdomain || null,
        {
          wristbandTenant: loginSignals.wristbandTenant,
          tenant_name: loginSignals.tenant_name,
          tenant_id: loginSignals.tenant_id,
          tenant_custom_domain: loginSignals.tenant_custom_domain
        }
      ).toString();
    })();

    return () => { cancelled = true; };
  }, [location, isLoaded, isSignedIn]);

  useEffect(() => {
    if (!authorizationRequestToken) {
      return undefined;
    }

    const restartStaleForm = () => {
      if (!storedAuthRequestIsStale()) {
        return;
      }
      if (sessionStorage.getItem(SIGN_IN_RESTART_ONCE_KEY) === '1') {
        return;
      }
      sessionStorage.setItem(SIGN_IN_RESTART_ONCE_KEY, '1');
      const typed = email.trim().toLowerCase();
      if (typed) {
        sessionStorage.setItem(SIGN_IN_EMAIL_KEY, typed);
      }
      clearStoredAuthRequest();
      persistProtectedReturnPath(location);
      window.location.href = buildTenantLoginUrl().toString();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        restartStaleForm();
      }
    };

    window.addEventListener('focus', restartStaleForm);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', restartStaleForm);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [authorizationRequestToken, email, location]);

  /**
   * Handle form submission
   *
   * Normal flow (we have authorizationRequestToken):
   * 1. Call backend to verify password with authorizationRequestToken
   * 2. Backend returns redirectUrl to Wristband create-auth-session
   * 3. Follow redirect to complete OAuth flow (Wristband -> callback -> tokens)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Safety fallback: If somehow no token, start OAuth flow
    if (!authorizationRequestToken) {
      const loginUrl = buildTenantLoginUrl();
      loginUrl.searchParams.set('email', email.trim().toLowerCase());
      persistProtectedReturnPath(location);
      window.location.href = loginUrl.toString();
      return;
    }

    try {
      const response = await apiClient.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
        authorizationRequestToken
      });

      if (response.data.success && response.data.redirectUrl) {
        sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
        window.location.href = response.data.redirectUrl;
      } else {
        setError('Unexpected response from server. Please try again.');
        setIsSubmitting(false);
      }

    } catch (err) {
      console.error('Login error:', err);

      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (shouldRestartSignInFlow(errorCode)) {
        const typed = email.trim().toLowerCase();
        if (typed) {
          sessionStorage.setItem(SIGN_IN_EMAIL_KEY, typed);
        }
        clearStoredAuthRequest();
        persistProtectedReturnPath(location);
        window.location.href = buildTenantLoginUrl().toString();
        return;
      }

      setError(resolveSignInError({
        errorCode,
        errorMessage,
        httpStatus: err.response?.status,
        portal: 'merchant',
      }));
      setIsSubmitting(false);
    }
  };

  // Keep HTML #boot-splash up until the request token (or a real error) is ready.
  // A second React splash remounts and restarts the wheel.
  if (!isLoaded || isCheckingAuth) {
    return <BootSplashKeeper />;
  }

  return (
    <>
    <BootSplashDismisser ready />
    <AuthLayout
      title={<>Your business<br /><span className="text-brand">in one tab.</span></>}
      subtitle="Effortlessly manage your daily workflows, reporting, and team from a single, high-performance interface."
      features={signInFeatures}
      helper={showSignupChrome ? (
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-gray-500">Don't have an account?</span>
          <Link
            to="/sign-up"
            className="font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Create one
          </Link>
        </div>
      ) : null}
    >
      <div className="space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Welcome back</h1>
          <p className="text-gray-500 text-sm font-medium">Sign in to your dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:border-brand/50 focus:ring-4 focus:ring-brand/10 outline-none"
                placeholder="name@company.com"
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center ml-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-bold text-brand hover:text-brand-hover transition-colors"
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:border-brand/50 focus:ring-4 focus:ring-brand/10 outline-none"
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 shadow-2xl"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        <p className="text-center text-[10px] text-gray-600 leading-relaxed max-w-[280px] mx-auto">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="text-brand font-bold hover:underline">Terms</Link> and{' '}
          <Link to="/privacy" className="text-brand font-bold hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </AuthLayout>
    </>
  );
}

export default SignIn;
